// Dispatcher-facing charter lead client. Hits /api/charter/dispatch/leads (operations role) on the
// Java backend — the SAME rows the agents service writes through Java. Auth-header pattern mirrors
// adminApi (prefer a fresh Cognito session token).
import { getCurrentSession } from '../services/authService';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

async function authHeaders(authSession) {
  let token = authSession?.idToken;
  try { const s = await getCurrentSession(); token = s.idToken; } catch (e) { /* keep prop token */ }
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request(path, authSession) {
  const headers = await authHeaders(authSession);
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (res.status === 404) return null;
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try { const body = await res.json(); detail = body.error || detail; } catch (e) { /* non-JSON */ }
    throw new Error(detail);
  }
  return res.json().catch(() => null);
}

// Recent leads for the dashboard list (denormalized summaries).
export const listCharterLeads = (authSession) =>
  request('/api/charter/dispatch/leads', authSession);

// Full lead detail (state + transcript) for the selected session.
export const getCharterLead = (sessionId, authSession) =>
  request(`/api/charter/dispatch/leads/${encodeURIComponent(sessionId)}`, authSession);

// The dispatcher "book it" action: approve a submitted lead into a reservation. Idempotent on the
// backend. Returns the booking on 201; on 422 (not bookable) / 404 (unknown) it throws an Error whose
// `.status` is the HTTP code and whose message is the backend's explanation.
export async function approveCharterLead(sessionId, authSession) {
  const headers = await authHeaders(authSession);
  const res = await fetch(
    `${API_BASE}/api/charter/dispatch/leads/${encodeURIComponent(sessionId)}/approve`,
    { method: 'POST', headers },
  );
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.message || body.error)) || `Approve failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// The dispatcher "edit the draft" action: re-shape stops + the dispatcher fee/coach/hotel knobs and
// re-price. On a SUBMITTED lead this also refreshes the submit-time freeze the booking uses. `edit` is
// a CharterDispatchEditRequest (each field optional; null leaves the snapshot value untouched).
// Returns the re-priced quote on 200; on 422 (not editable / re-price rejected) or 404 it throws an
// Error whose `.status` is the HTTP code and whose message is the backend's explanation.
export async function editCharterLeadQuote(sessionId, edit, authSession) {
  const headers = await authHeaders(authSession);
  const res = await fetch(
    `${API_BASE}/api/charter/dispatch/leads/${encodeURIComponent(sessionId)}/edit`,
    { method: 'POST', headers, body: JSON.stringify(edit || {}) },
  );
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.message || body.error)) || `Edit failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}
